import React, { useState, useEffect } from 'react';
import { useLocation, Routes, Route, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { useMobile } from '../../../shared/hooks/useMobile';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { videosService, Video } from '../../../shared/services/videosService';
import { useCaptureData } from '../hooks/useCaptureData';
import { useJobMonitoring } from '../hooks/useJobMonitoring';
import { useUploadManager } from '../hooks/useUploadManager';
import { CaptureItem, ToastState } from '../types';
import UploadVideoModal from '../components/UploadVideoModal';
import EditVideoModal from '../components/EditVideoModal';
import DeleteVideoModal from '../components/DeleteVideoModal';
import VideoDetailsDrawer from '../components/VideoDetailsDrawer';
import CaptureSidebar from '../components/CaptureSidebar';
import FolderTree from '../components/FolderTree';
import FileExplorer from '../components/FileExplorer';
import UploadPage from './UploadPage';
import HighlightsTacticsPage from './HighlightsTacticsPage';
import StorageUsagePage from './StorageUsagePage';
import SocialMediaUploadsPage from './SocialMediaUploadsPage';
import SocialMediaUploadDetailPage from './SocialMediaUploadDetailPage';
import HighlightDetailPage from './HighlightDetailPage';
import VideoDetailPage from './VideoDetailPage';
import SocialMediaSettingsPage from './SocialMediaSettingsPage';
import Toast from '../../../shared/components/ui/Toast';
import Button from '../../../shared/components/ui/Button';

const CaptureApp: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentOrganization } = useOrg();
  const { isMobile } = useMobile();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobile);
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || 'default';
  const userId = user?.id || '';
  
  // State
  const [selectedFolderPath, setSelectedFolderPath] = useState<string>('');
  const [expandedFolders, setExpandedFolders] = useState(() => new Set<string>(['root']));
  const [globalSearch, setGlobalSearch] = useState('');
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<Video | null>(null);
  const [editVideo, setEditVideo] = useState<Video | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<CaptureItem | null>(null);
  const [isVideoDetailsDrawerOpen, setIsVideoDetailsDrawerOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteVideoModalOpen, setIsDeleteVideoModalOpen] = useState(false);
  const [deleteVideoData, setDeleteVideoData] = useState<{ videoId: string; videoName: string; storagePath: string } | null>(null);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    type: 'info',
    message: ''
  });
  const [stuckVideos, setStuckVideos] = useState<Video[]>([]);

  const activeNav = location.pathname.includes('/storage-usage')
    ? 'storage-usage'
    : location.pathname.includes('/match-assets')
    ? 'match-assets'
    : location.pathname.includes('/social-media-settings')
    ? 'social-media-integrations'
    : location.pathname.includes('/social-media-uploads') || location.pathname.includes('/social-media-upload/')
    ? 'social-media-uploads'
    : 'explorer';

  const sidebarNav = [
    { id: 'explorer', label: 'Video Library', icon: '🗂️', disabled: false },
    { id: 'match-assets', label: 'Match Assets', icon: '⚡', disabled: false },
    { id: 'social-media-uploads', label: 'Social Media Uploads', icon: '📤', disabled: false },
    { id: 'social-media-integrations', label: 'Social Media Integrations', icon: '🔗', disabled: false },
    { id: 'storage-usage', label: 'Usage', icon: '💾', disabled: false },
    { id: 'processing', label: 'Processing queue', icon: '⚙️', disabled: true },
    { id: 'recordings', label: 'Recordings', icon: '🎥', disabled: true },
    { id: 'dashboard', label: 'Go to Dashboard', icon: '🏠', disabled: false }
  ] as const;

  // Custom hooks
  const { folders, files, folderTree, isLoading, videosByFolder, loadFolderContents, loadFolderTree } = useCaptureData(orgId);
  const { uploadingFiles, handleUploadComplete } = useUploadManager();

  // Job monitoring
  useJobMonitoring({
    orgId,
    files,
    selectedFolderPath,
    loadFolderContents,
    setToast,
  });

  // Check for stuck videos (for highlighting only - users can deactivate via edit modal)
  useEffect(() => {
    const checkStuckVideos = async () => {
      const result = await videosService.getStuckVideos(orgId, 1); // Videos stuck for > 1 hour
      if (result.success && result.data) {
        setStuckVideos(result.data);
      }
    };

    checkStuckVideos();
    // Check every 30 seconds
    const interval = setInterval(checkStuckVideos, 30000);
    return () => clearInterval(interval);
  }, [orgId]);

  // Handlers
  const toggleFolder = (folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleFolderClick = (folder: CaptureItem) => {
    const folderPath = folder.path || '';
    setSelectedFolderPath(folderPath);
    loadFolderContents(folderPath);
  };

  const handleBreadcrumbClick = (crumb: CaptureItem) => {
    if (crumb.id === 'root') {
      setSelectedFolderPath('');
      loadFolderContents('');
    } else if (crumb.path) {
      setSelectedFolderPath(crumb.path);
      loadFolderContents(crumb.path);
    }
  };

  const handleRefresh = () => {
    loadFolderContents(selectedFolderPath);
    loadFolderTree();
  };

  const handleViewVideo = async (videoId: string) => {
    // Open drawer for video details
    const result = await videosService.getVideoById(videoId);
    if (result.success && result.data) {
      setSelectedVideo(result.data);
      setSelectedAsset(null);
      setIsVideoDetailsDrawerOpen(true);
    }
  };

  const handleRowClickVideo = (videoId: string) => {
    // Navigate to video detail page on row click
    navigate(`/o/${orgId}/capture/video/${videoId}`);
  };

  const handleEditVideo = async (videoId: string) => {
    const result = await videosService.getVideoById(videoId);
    if (result.success && result.data) {
      setEditVideo(result.data);
      setIsEditModalOpen(true);
    }
  };

  const handleEditSuccess = async (updatedVideo: Video) => {
    setToast({
      isVisible: true,
      type: 'success',
      message: 'Video details updated successfully.',
    });
    // Reload folder tree first, then reload folder contents
    await loadFolderTree();
    await loadFolderContents(selectedFolderPath);
    // If video is now inactive, close drawer if it's open for this video
    if (!updatedVideo.is_active && selectedVideo?.id === updatedVideo.id) {
      setIsVideoDetailsDrawerOpen(false);
      setSelectedVideo(null);
      setSelectedAsset(null);
    }
  };

  const handleViewAsset = (asset: CaptureItem) => {
    // Open drawer for eye icon click
    setSelectedAsset(asset);
    setSelectedVideo(asset.video || null);
    setIsVideoDetailsDrawerOpen(true);
  };

  const handleRowClickAsset = (asset: CaptureItem) => {
    // Navigate to detail page on row click (for assets that have detail pages)
    // Currently no assets in Video Library have detail pages, so this is a placeholder
  };

  const handleUploadCompleteWrapper = () => {
    handleUploadComplete(() => {
      loadFolderContents(selectedFolderPath);
      loadFolderTree();
    }, setToast);
  };

  const handleDeleteVideo = (videoId: string, videoName: string, storagePath: string) => {
    setDeleteVideoData({ videoId, videoName, storagePath });
    setIsDeleteVideoModalOpen(true);
  };

  const handleDeleteVideoSuccess = () => {
    loadFolderContents(selectedFolderPath);
    loadFolderTree();
    setIsDeleteVideoModalOpen(false);
    setDeleteVideoData(null);
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({
      isVisible: true,
      type,
      message
    });
  };

  // Check if we're on the upload route
  const isUploadRoute = location.pathname.includes('/upload/');
  const isStorageUsageRoute = location.pathname.includes('/storage-usage');

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {!isUploadRoute && (
        <>
      {isMobile && !isSidebarCollapsed && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40"
          onClick={() => setIsSidebarCollapsed(true)}
        />
      )}

      {/* Sidebar */}
      <CaptureSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        isMobile={isMobile}
        orgId={orgId}
        activeNav={activeNav}
        sidebarNav={sidebarNav}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Only show header if not on detail pages */}
        {!location.pathname.includes('/highlight/') && !location.pathname.includes('/video/') && !location.pathname.includes('/social-media-upload/') && (
          <header className="border-b border-gray-200 bg-white px-4 sm:px-6 py-3">
            <div className="w-full grid gap-3 sm:grid-cols-[auto,1fr,auto] sm:items-center">
              <div className="flex flex-col">
                <p className="text-xs text-gray-500 uppercase tracking-wider">Capture</p>
                <h1 className="text-xl font-semibold text-gray-900">
                  {location.pathname.includes('/storage-usage')
                    ? 'Usage'
                    : location.pathname.includes('/match-assets')
                    ? 'Match Assets'
                    : location.pathname.includes('/social-media-settings')
                    ? 'Social Media Settings'
                    : location.pathname.includes('/social-media-uploads')
                    ? 'Social Media Uploads'
                    : 'Library'}
                </h1>
              </div>

              {/* Centered Search Bar - Hide for settings page */}
              {!isStorageUsageRoute && !location.pathname.includes('/social-media-settings') && (
                <div className="flex justify-center order-3 sm:order-2">
                  <div className="relative w-full max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-2 sm:pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={globalSearch}
                      onChange={(e) => setGlobalSearch(e.target.value)}
                      placeholder={location.pathname.includes('/social-media-uploads') 
                        ? "Search uploads..." 
                        : "Search across your organization..."}
                      className="block w-full pl-8 sm:pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
                    />
                    {globalSearch && (
                      <button
                        onClick={() => setGlobalSearch('')}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center min-h-44 min-w-44"
                      >
                        <svg className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Right-aligned +Video Button - Only show for video library (default route) */}
              {!isStorageUsageRoute && 
               !location.pathname.includes('/social-media-settings') && 
               !location.pathname.includes('/social-media-uploads') &&
               !location.pathname.includes('/match-assets') && (
                <div className="flex justify-end order-2 sm:order-3">
                  <Button
                    onClick={() => setIsUploadModalOpen(true)}
                    variant="primary"
                    size={isMobile ? "sm" : "md"}
                    className="min-h-44"
                  >
                    + Video
                  </Button>
                </div>
              )}
            </div>
          </header>
        )}

        <main className="flex-1 overflow-y-auto">
              <Routes>
                <Route
                  path="match-assets"
                  element={<HighlightsTacticsPage />}
                />
                <Route
                  path="social-media-uploads"
                  element={<SocialMediaUploadsPage globalSearch={globalSearch} />}
                />
                <Route
                  path="social-media-upload/:uploadId"
                  element={<SocialMediaUploadDetailPage />}
                />
                <Route
                  path="social-media-settings"
                  element={<SocialMediaSettingsPage />}
                />
                <Route
                  path="highlight/:highlightId"
                  element={<HighlightDetailPage />}
                />
                <Route
                  path="video/:videoId"
                  element={<VideoDetailPage />}
                />
                <Route
                  path="storage-usage"
                  element={<StorageUsagePage />}
                />
                <Route
                  path="upload/:videoId"
                  element={<UploadPage />}
                />
                <Route
                  path="*"
                  element={
          <div className="p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="w-full lg:w-64 bg-white border border-gray-200 rounded-xl">
                <div className="p-4 space-y-1">
                  <FolderTree
                    node={folderTree}
                    expandedFolders={expandedFolders}
                    selectedFolderPath={selectedFolderPath}
                    onToggleFolder={toggleFolder}
                    onFolderClick={handleFolderClick}
                  />
                </div>
            </div>

              <div className="flex-1">
                <FileExplorer
                  folders={folders}
                  files={files}
                  videosByFolder={videosByFolder}
                  uploadingFiles={uploadingFiles}
                  selectedFolderPath={selectedFolderPath}
                  globalSearch={globalSearch}
                  isLoading={isLoading}
                  stuckVideoIds={new Set(stuckVideos.map((v) => v.id))}
                  stuckVideos={stuckVideos}
                  onFolderClick={handleFolderClick}
                  onBreadcrumbClick={handleBreadcrumbClick}
                  onRefresh={handleRefresh}
                  onViewVideo={handleViewVideo}
                  onViewAsset={handleViewAsset}
                  onRowClickVideo={handleRowClickVideo}
                  onRowClickAsset={handleRowClickAsset}
                  onEditVideo={handleEditVideo}
                  onDeleteVideo={handleDeleteVideo}
                />
          </div>
        </div>
      </div>
                  }
                />
              </Routes>
            </main>
          </div>
        </>
      )}

      {isUploadRoute && (
        <main className="flex-1 overflow-y-auto">
          <Routes>
            <Route
              path="upload/:videoId"
              element={<UploadPage />}
            />
          </Routes>
        </main>
      )}

      {/* Upload Modal */}
      <UploadVideoModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        orgId={orgId}
        userId={userId}
        currentFolderPath={selectedFolderPath}
        onUploadComplete={handleUploadCompleteWrapper}
      />

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        onClose={() => setToast(prev => ({ ...prev, isVisible: false }))}
        type={toast.type}
        message={toast.message}
        duration={5000}
      />


      {/* Video Details Drawer */}
      <VideoDetailsDrawer
        isOpen={isVideoDetailsDrawerOpen}
        onClose={() => {
          setIsVideoDetailsDrawerOpen(false);
          setSelectedVideo(null);
          setSelectedAsset(null);
        }}
        video={selectedVideo}
        asset={selectedAsset}
        onToast={(message, type) => {
          setToast({
            isVisible: true,
            type,
            message,
          });
        }}
        onDeleteVideo={handleDeleteVideo}
      />

      {/* Delete Video Modal */}
      {deleteVideoData && (
        <DeleteVideoModal
          isOpen={isDeleteVideoModalOpen}
          onClose={() => {
            setIsDeleteVideoModalOpen(false);
            setDeleteVideoData(null);
          }}
          orgId={orgId}
          videoId={deleteVideoData.videoId}
          videoName={deleteVideoData.videoName}
          storagePath={deleteVideoData.storagePath}
          onSuccess={handleDeleteVideoSuccess}
          onToast={showToast}
        />
      )}

      <EditVideoModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditVideo(null);
        }}
        video={editVideo}
        onSuccess={handleEditSuccess}
      />
    </div>
  );
};

export default CaptureApp;
